import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { apiService } from '../services/apiService';
import UserAvatar from '../components/UserAvatar';
import { useTvFreshness, tvLoadErrorMessage } from '../hooks/useTvFreshness';
import { useOnReconnect } from '../hooks/useOnReconnect';
import { useTvStage } from '../hooks/useTvStage';
import { formatSavedAt } from '../services/connectionStatus';
import { readTvSnapshot, writeTvSnapshot } from '../utils/tvSnapshot';
import { eventImage } from '../components/EventsBrowse';
import { isTournament } from '../components/TournamentDetailsFields';
import './HallwayTv.css';

/**
 * The hallway sign: the first screen a visitor sees coming through the door.
 *
 * Runs unattended on the entryway TV, public and signed out, at a fixed
 * 1920x1080 stage scaled to the panel (hooks/useTvStage.js). The left side
 * types out a rotating greeting, introduces today's hosts, and shows how a
 * guest pays. The right side lists today's events (until they end), what is coming up,
 * and the next tournament.
 * A footer carries the club's QR codes and the clock.
 *
 * Hosts: a hosted event's hosts go up an hour before it starts and come down
 * when it ends. Back to back, the next event's hosts wait for the earlier one to
 * end (at the latest, until their own start). One event with one host gets a
 * single card; two hosted events, or one event with co-hosts, get two columns
 * side by side (the first two either way).
 *
 * Private events (events.is_private, an admin setting) only introduce their
 * hosts: the feed sends no title, description or artwork for them, so the host
 * card shows the hosting times, or the event's own hallway label if it has one
 * (event_metadata.hallway_label). They never appear under Coming up.
 *
 * Notices: an event can carry a hallway sign notice (event_metadata.hallway_notice,
 * set from the Edit Event form): a quiet room with no pinball, or a maintenance
 * day. It takes the greeting's place as a hazard-tape banner on the same window as
 * hosts: from an hour before the event starts until it ends (onSignNow).
 *
 * Designed in Claude Design as "Hallway Sign v5".
 *
 * Preview: ?event=<id> shows the sign as it will look when that event starts,
 * with the clock stopped there and PREVIEW in the footer. Useful for checking an
 * event and its hosts (bios, photos, private labels, notices) before the night.
 */

// Where each QR on the sign goes. A link left as null hides its tile rather
// than pointing a QR at nothing.
const GUEST_PASS_URL = 'https://checkout.square.site/merchant/ML48B3WVH31MB/checkout/RWQVGMIKLUM6NNMBKOE4XGDX';
const GUEST_PASS_PRICE = '$15';
// Always production, even on a dev build, so the QR can be tested from any phone.
const APPLY_URL = 'https://app.capitalpinballclub.com/apply';
const WIFI = { ssid: 'CapitalPinball', password: 'CapPinMembers' };
const DISCORD_URL = 'https://discord.gg/MCSuzKdZP';
const RULES_URL = 'https://capitalpinballclub.com/home-1/rules-and-faq/';

const REFRESH_INTERVAL_MS = 60000;
// How many events "Coming up" lists. The rows tighten above four.
const EVENT_COUNT = 4;
// The left column needs room for the guest pass card (title, price and QR in one row)
// and the footer for four QR tiles and the clock.
const MIN_STAGE_WIDTH = 1880;
const SNAPSHOT_KEY = 'hallway-tv';

const timeLabel = (date) => date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

// The line under a host. A private event with no hallway label shows only its hosting times.
const endOf = (event) => new Date(event.date.getTime() + (event.duration_minutes || 240) * 60000);
const eventLine = (event) => {
  if (event.title) return `${event.title} · ${timeLabel(event.date)}`;
  return `${timeLabel(event.date)} to ${timeLabel(endOf(event))}`;
};
const longDate = (date) => date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

const startOfDay = (date) => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};

// The QR payload a phone camera offers to join a network with.
const escapeWifi = (value) => value.replace(/([\\;,:"])/g, '\\$1');
const wifiPayload = ({ ssid, password }) => `WIFI:T:WPA;S:${escapeWifi(ssid)};P:${escapeWifi(password)};;`;

// The tournament card's format lines, from the tournament event form's IFPA format picks
// (PinballEventForm). A line only shows when its format is set.
const formatLines = (event) => {
  const meta = event.event_metadata || {};
  const lines = [];
  if (meta.qualifier_format) {
    lines.push(['QUALIFYING', meta.num_rounds ? `${meta.num_rounds} rounds of ${meta.qualifier_format}` : meta.qualifier_format]);
  }
  if (meta.finals_format) {
    lines.push(['FINALS', meta.finals_format]);
  }
  return lines;
};

// "12:00 PM" reads as "12 PM" on the tournament card. Check-in is typed text, so this
// only trims a whole hour and leaves anything else as written.
const shortTime = (text) => text.replace(/:00(?=\s*[AP]M)/i, '');

const GREETINGS = [
  "Glad you're here", 'Welcome to the club', 'Good to see you', 'Come on in', 'Hey there',
  'Your third space', 'Have a ball', 'Nice to meet you', 'Welcome back', 'Game on', 'More than pinball'
];
const GREETING_HOLD_MS = 6000;

// The banner for each kind of notice. Room names come from the event.
const NOTICES = {
  quiet_room: {
    color: '#FAC508',
    label: 'QUIET ROOM TONIGHT',
    text: (notice) => `No pinball in the ${notice.room}`
  },
  maintenance: {
    color: '#F59E0B',
    label: 'MAINTENANCE DAY',
    text: () => 'Techs are working on every machine today. Pardon the bright lights.'
  }
};

const noticeOf = (event) => {
  const notice = event.event_metadata?.hallway_notice;
  if (!notice || !NOTICES[notice.kind]) return null;
  if (notice.kind === 'quiet_room' && !notice.room?.trim()) return null;
  return notice;
};

const HOST_LEAD_MS = 60 * 60000;

/**
 * Which of today's events are on the sign right now: each goes up an hour before it starts
 * and comes down when it ends. The lead-in never cuts into an earlier event of the same kind
 * that is still running: the next one waits for it to end, and at the latest for its own start.
 * Hosts and notices both use this, so a quiet room banner comes down with its event's hosts
 * instead of staying up until midnight.
 */
function onSignNow(todaysEvents, clock) {
  return todaysEvents.filter((event) => {
    const start = event.date.getTime();
    const earlierEnds = todaysEvents
      .filter((other) => other.date < event.date)
      .map((other) => endOf(other).getTime());
    const showFrom = Math.min(start, Math.max(start - HOST_LEAD_MS, ...earlierEnds));
    return clock.getTime() >= showFrom && clock < endOf(event);
  });
}

/**
 * Today's host columns. Two hosted events show one column each (co-hosts of
 * one of them share its column); one event with co-hosts shows a column per
 * host. Bios only appear where a column is one person.
 */
function hostColumns(hostedToday) {
  if (hostedToday.length > 1) {
    return hostedToday.slice(0, 2).map((event) => ({
      key: `event-${event.id}`,
      name: event.hosts.map((host) => host.name).join(' & '),
      photo: event.hosts.length === 1 ? event.hosts[0].photo : null,
      bio: event.hosts.length === 1 ? event.hosts[0].bio : null,
      event
    }));
  }
  const [event] = hostedToday;
  return event ? event.hosts.slice(0, 2).map((host) => ({
    key: `host-${host.name}`,
    name: host.name,
    photo: host.photo,
    bio: host.bio,
    event
  })) : [];
}

// Font sizing for the greeting. Each greeting is sized so the whole phrase fits its space,
// measured once per phrase rather than per keystroke so the text does not jump while typing.
let measureContext = null;
function greetingSize(text, max, room) {
  if (!measureContext) measureContext = document.createElement('canvas').getContext('2d');
  const family = getComputedStyle(document.documentElement).getPropertyValue('--ds-global-font-familyBody') || 'sans-serif';
  measureContext.font = `800 64px ${family}`;
  const width = measureContext.measureText(text).width * 0.97 + 14;
  return Math.min(max, Math.floor((64 * room) / width));
}

/** The welcome line: types a greeting, holds it, deletes it, types the next. */
function Typer({ max, room }) {
  const [state, setState] = useState({ index: 0, length: GREETINGS[0].length, phase: 'hold' });
  // Re-measure once the web font has loaded, since the fallback font is a different width.
  const [, setFontsReady] = useState(false);

  useEffect(() => {
    let live = true;
    document.fonts?.ready.then(() => live && setFontsReady(true));
    return () => { live = false; };
  }, []);

  useEffect(() => {
    const { index, length, phase } = state;
    const word = GREETINGS[index];
    let delay;
    let next;
    if (phase === 'hold') {
      delay = GREETING_HOLD_MS;
      next = { ...state, phase: 'delete' };
    } else if (phase === 'delete') {
      delay = length > 0 ? 40 : 450;
      next = length > 0
        ? { ...state, length: length - 1 }
        : { index: (index + 1) % GREETINGS.length, length: 0, phase: 'type' };
    } else {
      delay = 85 + Math.random() * 60;
      next = length + 1 >= word.length
        ? { ...state, length: word.length, phase: 'hold' }
        : { ...state, length: length + 1 };
    }
    const timer = setTimeout(() => setState(next), delay);
    return () => clearTimeout(timer);
  }, [state]);

  const full = GREETINGS[state.index];
  const size = greetingSize(full, max, room);
  return (
    <p className="hwtv-typer" style={{ height: max * 1.12, fontSize: size }}>
      {full.slice(0, state.length)}
      <span
        className="hwtv-typer__caret"
        style={{ width: Math.max(4, size * 0.09), height: size * 0.94 }}
      />
    </p>
  );
}

function WarnIcon() {
  return (
    <span className="hwtv-notice__icon" aria-hidden="true">
      <svg width="60" height="54" viewBox="0 0 42 38">
        <path d="M21 2.5 L39.5 35.5 H2.5 Z" fill="#EF4444" stroke="#EF4444" strokeWidth="3" strokeLinejoin="round" />
        <rect x="19" y="13" width="4" height="12" rx="2" fill="#fff" />
        <circle cx="21" cy="30" r="2.4" fill="#fff" />
      </svg>
    </span>
  );
}

function Notice({ notice }) {
  const spec = NOTICES[notice.kind];
  return (
    <div className="hwtv-notice">
      <div
        className="hwtv-notice__tape"
        style={{ background: `repeating-linear-gradient(-45deg, ${spec.color} 0 18px, #14041E 18px 36px)` }}
      >
        <div className="hwtv-notice__panel">
          <WarnIcon />
          <div className="hwtv-notice__text">
            <p className="hwtv-notice__label" style={{ color: spec.color }}>{spec.label}</p>
            <p className="hwtv-notice__body">{spec.text(notice)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/** A host's profile photo, or their initials (the shared UserAvatar). */
function Avatar({ host, size }) {
  return <UserAvatar person={host} className="hwtv-host__avatar" style={{ width: size, height: size }} />;
}

function QrTile({ value, size, className = 'hwtv-qr' }) {
  return (
    <div className={className} style={{ width: size, height: size }}>
      <QRCode value={value} size={size - 16} level="M" />
    </div>
  );
}

/**
 * The animated club logo: a seamless 30-second loop with an alpha channel, the
 * same proportions as logo.png, so it shares the static logo's class. If the
 * panel can't play it, the static logo takes its place.
 *
 * logo-loop.webm is encoded at display size on purpose (578x600, 30fps, VP9
 * with alpha). Browsers decode alpha VP9 in software, and the hallway Fire TV's
 * weak CPU stuttered on the original 2030x2106 60fps export. Re-encode at
 * roughly the on-screen size if the source is ever replaced.
 */
function LogoLoop() {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return <img className="hwtv-welcome__logo" src="/logo.png" alt="Capital Pinball" />;
  }
  return (
    <video
      className="hwtv-welcome__logo"
      src="/logo-loop.webm"
      poster="/logo.png"
      autoPlay
      loop
      muted
      playsInline
      aria-label="Capital Pinball"
      onError={() => setFailed(true)}
    />
  );
}

function HallwayTv() {
  const [params] = useSearchParams();
  // ?event=<id> previews the sign as it will look when that event starts.
  const previewId = parseInt(params.get('event'), 10) || null;
  // The last live data this browser saw, so a reload with the server down
  // still shows the sign, marked as not live (utils/tvSnapshot.js). A preview
  // neither shows nor overwrites it.
  const [snapshot] = useState(() => (previewId ? null : readTvSnapshot(SNAPSHOT_KEY)));
  const [events, setEvents] = useState(() => snapshot?.data.events ?? null);
  const [previewAt, setPreviewAt] = useState(null);
  const [error, setError] = useState(null);
  // The footer carries a clock, so re-read it every 15 seconds.
  const [now, setNow] = useState(() => new Date());
  const { stale, asOf, noteResponses } = useTvFreshness(snapshot?.savedAt ?? null);
  const stage = useTvStage(MIN_STAGE_WIDTH);

  const fetchData = useCallback(async () => {
    try {
      const response = await apiService.getTvEvents(previewId);
      setEvents(response.data.events);
      if (previewId) {
        setPreviewAt(new Date(response.data.previewAt));
      } else if (noteResponses([response])) {
        writeTvSnapshot(SNAPSHOT_KEY, response.data);
      }
      setError(null);
    } catch (err) {
      setError(previewId && err.response?.status === 404
        ? `No event ${previewId} to preview.`
        : tvLoadErrorMessage(err, 'Unable to load events.'));
    }
  }, [noteResponses, previewId]);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  // The server is back: reload now rather than at the next refresh.
  useOnReconnect(fetchData);

  useEffect(() => {
    const intervalId = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(intervalId);
  }, []);

  // The board follows the clock to the minute, so events drop off "Coming up" as they start.
  // A preview stops the clock at the previewed event's start.
  const clockTime = previewAt || now;
  const minuteKey = Math.floor(clockTime.getTime() / 60000);

  const board = useMemo(() => {
    const clock = new Date(minuteKey * 60000);
    const today = startOfDay(clock);
    // event_date is club-local wall time with no zone, which Date reads as local.
    const upcoming = (events || [])
      .map((event) => ({ ...event, hosts: event.hosts || [], date: new Date(event.event_date) }))
      .filter((event) => event.date >= today)
      .sort((a, b) => a.date - b.date);
    const todays = upcoming.filter((event) => event.date.toDateString() === clock.toDateString());
    const hostedToday = todays.filter((event) => event.hosts.length > 0);
    const columns = hostColumns(onSignNow(hostedToday, clock));
    const featured = new Set(columns.map((column) => column.event));
    // A notice is up on the same window as hosts: from an hour before its event to its end.
    const notice = onSignNow(todays.filter((event) => noticeOf(event)), clock)
      .map(noticeOf)
      .find(Boolean) || null;
    // A private event is only ever here to introduce its hosts.
    const listed = upcoming.filter((event) => !event.is_private);
    const tournament = listed.find(isTournament) || null;
    // Today's events lead the list, even ones whose hosts are already up on the left, and
    // stay until they end rather than when they start, so the board always says what is on
    // right now. Later days drop off as they start, like before.
    const isTodays = (event) => event.date.toDateString() === clock.toDateString();
    const later = listed
      .filter((event) => (isTodays(event)
        ? endOf(event) > clock
        : !featured.has(event) && event !== tournament && event.date > clock))
      .map((event) => ({ ...event, isToday: isTodays(event), isLive: event.date <= clock }))
      .slice(0, EVENT_COUNT);
    return { columns, notice, tournament, later };
  }, [events, minuteKey]);

  if (!events) {
    return (
      <div className="hwtv-root">
        <div className="hwtv-boot">{error || 'Loading...'}</div>
      </div>
    );
  }

  const { columns, notice, tournament, later } = board;
  const solo = columns.length === 1 ? columns[0] : null;
  const tournamentImage = tournament ? eventImage(tournament) : null;
  const signUpUrl = tournament?.event_metadata?.matchplay_url || null;
  const tournamentFormat = tournament ? formatLines(tournament) : [];
  const savedAt = stale && asOf ? formatSavedAt(asOf, now) : null;
  const hasWifi = Boolean(WIFI.ssid && WIFI.password);
  const links = [
    hasWifi && { key: 'wifi', value: wifiPayload(WIFI), label: 'WI-FI', title: WIFI.ssid },
    DISCORD_URL && { key: 'discord', value: DISCORD_URL, label: 'DISCORD', title: 'Join the club chat' },
    RULES_URL && { key: 'rules', value: RULES_URL, label: 'CLUB RULES', title: 'Read the full rules' },
    { key: 'apply', value: APPLY_URL, label: 'MEMBERSHIP', title: 'Apply here' }
  ].filter(Boolean);

  return (
    <div className="hwtv-root">
      <div
        className="hwtv-stage"
        style={{
          width: `${stage.width}px`,
          transform: `translate(-50%, -50%) scale(${stage.scale})`
        }}
      >
        <div className="hwtv-glow" />

        <div className="hwtv-body">
          <section className="hwtv-left">
            <div className="hwtv-welcome-area">
              {columns.length > 0 ? (
                <div className="hwtv-split">
                  <div className="hwtv-welcome">
                    <LogoLoop />
                    {notice ? (
                      <Notice notice={notice} />
                    ) : (
                      <div className="hwtv-welcome__fill"><Typer max={64} room={470} /></div>
                    )}
                  </div>
                  <div className="hwtv-host">
                    {solo ? (
                      <>
                        <p className="hwtv-host__label">TONIGHT'S HOST</p>
                        <Avatar host={solo} size={230} />
                        <div className="hwtv-host__heading">
                          <p className="hwtv-host__names">{solo.name}</p>
                          <p className="hwtv-host__event">{eventLine(solo.event)}</p>
                        </div>
                        {solo.bio && <p className="hwtv-host__bio">{solo.bio}</p>}
                      </>
                    ) : (
                      <>
                        <p className="hwtv-host__label">TONIGHT'S HOSTS</p>
                        <div className="hwtv-host__columns">
                          {columns.map((column) => (
                            <div className="hwtv-host__column" key={column.key}>
                              <Avatar host={column} size={170} />
                              <p className="hwtv-host__column-name">{column.name}</p>
                              <p className="hwtv-host__column-event">{eventLine(column.event)}</p>
                              {column.bio && <p className="hwtv-host__column-bio">{column.bio}</p>}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="hwtv-welcome">
                  <LogoLoop />
                  {notice ? (
                    <Notice notice={notice} />
                  ) : (
                    <div className="hwtv-welcome__fill"><Typer max={84} room={980} /></div>
                  )}
                </div>
              )}
            </div>

            <div className="hwtv-guest">
              <div className="hwtv-guest__text">
                <p className="hwtv-guest__title">Guests pay here</p>
                {hasWifi ? (
                  <>
                    <p className="hwtv-guest__sub">Join our Wi-Fi first (scan below), then scan to pay.</p>
                    <p className="hwtv-guest__aside">Your phone is lying to you about those bars.</p>
                  </>
                ) : (
                  <p className="hwtv-guest__sub">Scan to pay from your phone.</p>
                )}
              </div>
              <span className="hwtv-guest__price">{GUEST_PASS_PRICE}</span>
              <div className="hwtv-guest__qr">
                <QRCode value={GUEST_PASS_URL} size={170} level="M" />
              </div>
            </div>
          </section>

          <section className="hwtv-right">
            <div className="hwtv-upcoming">
              <p className="hwtv-eyebrow">COMING UP</p>
              <div className={`hwtv-upcoming__list ${later.length > 4 ? 'is-compact' : ''}`}>
                {later.map((event) => (
                  <div className={`hwtv-upcoming__row ${event.isToday ? 'is-today' : ''}`} key={event.id}>
                    <div className="hwtv-upcoming__date">
                      {event.isToday ? (
                        <span className="hwtv-upcoming__today">{event.isLive ? 'NOW' : 'TODAY'}</span>
                      ) : (
                        <>
                          <span className="hwtv-upcoming__dow">
                            {event.date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}
                          </span>
                          <span className="hwtv-upcoming__num">{event.date.getDate()}</span>
                        </>
                      )}
                    </div>
                    <div className="hwtv-upcoming__text">
                      <span className="hwtv-upcoming__event">{event.title}</span>
                      <span className="hwtv-upcoming__meta">
                        {event.isLive ? `Until ${timeLabel(endOf(event))}` : timeLabel(event.date)}
                        {event.hosts.length > 0 && ` · Hosted by ${event.hosts.map((host) => host.name).join(' & ')}`}
                      </span>
                    </div>
                  </div>
                ))}
                {later.length === 0 && <p className="hwtv-upcoming__empty">Nothing else on the calendar yet.</p>}
              </div>
            </div>

            {tournament && (
              <div className="hwtv-tourney">
                {/* The event photo sits behind the header under a purple overlay. */}
                <div
                  className={`hwtv-tourney__head ${signUpUrl ? '' : 'is-no-qr'}`}
                  style={tournamentImage ? { '--hwtv-tourney-image': `url("${tournamentImage}")` } : undefined}
                >
                  {/* Tournament sign-ups happen on MatchPlay, so that is where the QR goes. */}
                  {signUpUrl && (
                    <div className="hwtv-tourney__qr">
                      <QRCode value={signUpUrl} size={62} level="L" />
                    </div>
                  )}
                  <div className="hwtv-tourney__heading">
                    <p className="hwtv-tourney__eyebrow">NEXT TOURNAMENT</p>
                    <p className="hwtv-tourney__title">{tournament.title}</p>
                  </div>
                </div>
                <div className={`hwtv-tourney__body ${tournamentFormat.length ? '' : 'is-no-format'}`}>
                  <div className="hwtv-tourney__when">
                    <div className="hwtv-tourney__cal">
                      <span>{tournament.date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}</span>
                      <strong>{tournament.date.getDate()}</strong>
                      <span>{tournament.date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}</span>
                    </div>
                    <dl className="hwtv-tourney__times">
                      {tournament.event_metadata?.checkin_time && (
                        <div>
                          <dt>DOORS</dt>
                          <dd>{shortTime(tournament.event_metadata.checkin_time)}</dd>
                        </div>
                      )}
                      <div>
                        <dt>PLAY</dt>
                        <dd>{shortTime(timeLabel(tournament.date))}</dd>
                      </div>
                    </dl>
                  </div>
                  {tournamentFormat.length > 0 && (
                    <ol className="hwtv-tourney__steps">
                      {tournamentFormat.map(([label, value], index) => (
                        <li key={label}>
                          <span className="hwtv-tourney__step">{index + 1}</span>
                          <div>
                            <p className="hwtv-tourney__step-label">{label}</p>
                            <p className="hwtv-tourney__step-value">{value}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>

        <footer className="hwtv-footer">
          <div className="hwtv-links">
            {links.map((link) => (
              <div className="hwtv-link" key={link.key}>
                <QrTile value={link.value} size={84} />
                <div>
                  <p className="hwtv-link__label">{link.label}</p>
                  <p className="hwtv-link__title">{link.title}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="hwtv-clock">
            {previewId && <span className="hwtv-clock__test">PREVIEW · event {previewId}</span>}
            <span className="hwtv-clock__time">{timeLabel(clockTime)}</span>
            {savedAt ? (
              <span className="hwtv-clock__date">
                <span className="hwtv-dot" />Offline · as of {savedAt}
              </span>
            ) : (
              <span className="hwtv-clock__date">{longDate(clockTime)}</span>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

export default HallwayTv;
